import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Clock, User, LogOut, Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/landing/Logo";
import { api, useAuth } from "@/context/AuthContext";

/* Shared dashboard nav rail (matches the approved Homepage sidebar). */

const ITEMS = [
  { id: "home", label: "Home", icon: Home, path: "/homepage" },
  { id: "history", label: "History", icon: Clock, path: "/history" },
  { id: "friends", label: "Friends", icon: Users, path: "/friends" },
  { id: "profile", label: "Profile", icon: User, path: "/profile" },
];

/* Compact avatar shown on the Profile nav item — matches the composite
 * `picture = avatar_url || picture` returned by the backend and falls back to
 * the display name initial when neither is set. */
const ProfileAvatar = ({ user, isActive }) => {
  const picture = user?.picture;
  const initial = (user?.name || "U").charAt(0).toUpperCase();
  if (picture) {
    return (
      <img
        src={picture}
        alt={user?.name || "avatar"}
        data-testid="sidebar-avatar"
        className={`h-[22px] w-[22px] rounded-full object-cover ring-1 ${
          isActive ? "ring-[#E52525]/60" : "ring-white/10"
        }`}
      />
    );
  }
  return (
    <span
      data-testid="sidebar-avatar-fallback"
      className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-[10px] font-bold ${
        isActive ? "bg-[#E52525] text-white" : "bg-white/10 text-zinc-200"
      }`}
    >
      {initial}
    </span>
  );
};

/* Official Discord logo (simple-icons path, 24x24) rendered in currentColor
 * so it matches the sidebar's lucide icon sizing/tint exactly. */
const DiscordIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

const DISCORD_INVITE_URL = "https://discord.gg/ZxUnBHh8d";

export const DashboardSidebar = ({ active }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [friendsBadge, setFriendsBadge] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  // Light poll for pending incoming friend requests + practice invites so the
  // Friends nav item shows a red dot when someone reaches out.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const [f, i] = await Promise.all([
          api.get("/friends"),
          api.get("/friends/invites"),
        ]);
        if (!alive) return;
        setFriendsBadge(
          (f?.data?.pending_incoming?.length || 0) + (i?.data?.incoming?.length || 0)
        );
      } catch {
        /* ignore */
      }
    };
    tick();
    const iv = setInterval(tick, 6000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, []);

  // Check admin flag once — controls whether the Admin nav item appears.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/me");
        if (alive) setIsAdmin(!!r.data?.is_admin);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const items = [
    ...ITEMS,
    ...(isAdmin
      ? [
          {
            id: "admin-users",
            label: "Admin",
            icon: ShieldCheck,
            path: "/admin/users",
          },
        ]
      : []),
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const onItem = (item) => {
    if (item.path) navigate(item.path);
    else toast.info("Your profile is coming soon.");
  };

  return (
    <aside className="hidden w-64 shrink-0 flex-col justify-between border-r border-white/10 px-5 py-7 md:flex">
      <div>
        <div className="px-2">
          <Logo className="text-2xl" />
        </div>
        <nav className="mt-12 space-y-1.5">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.id;
            const isProfile = item.id === "profile";
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => onItem(item)}
                className={`relative flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold transition-colors duration-200 ${
                  isActive
                    ? "bg-[#E52525]/15 text-white"
                    : "text-zinc-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                {isProfile ? (
                  <ProfileAvatar user={user} isActive={isActive} />
                ) : (
                  <Icon size={18} className={isActive ? "text-[#E52525]" : ""} />
                )}
                {item.label}
                {item.id === "friends" && friendsBadge > 0 && (
                  <span
                    data-testid="friends-nav-badge"
                    className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#E52525] px-1.5 text-[10px] font-bold text-white"
                  >
                    {friendsBadge > 9 ? "9+" : friendsBadge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <div>
        <a
          data-testid="sidebar-discord-link"
          href={DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="Join the SalesPit Discord"
          aria-label="Join the SalesPit Discord"
          className="flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold text-zinc-400 transition-colors duration-200 hover:bg-white/5 hover:text-white"
        >
          <DiscordIcon size={18} />
          Discord
        </a>
        <button
          data-testid="sidebar-logout-btn"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold text-zinc-400 transition-colors duration-200 hover:bg-white/5 hover:text-white"
        >
          <LogOut size={18} />
          Log out
        </button>
      </div>
    </aside>
  );
};
